import classNames from 'classnames';
import * as faceapi from 'face-api.js';
import { useCallback, useEffect, useRef, useState } from 'react';

import { RemoteFeedObject } from 'vcs-common';

import { ArrowGreaterThan } from '../../icons/arrow-greater-then';
import { MicroIcon } from '../../icons/micro';
import { CameraOffBackdrop, CameraOffLabel } from '../camera-off-ui';
import { RaisedHandCornerIndicator } from '../raised-hand-corner-indicator';

const MAX_DISPLAY_NAME_LENGTH = 15;

function truncateDisplayName(name: string): string {
  if (!name) return '';
  return name.length > MAX_DISPLAY_NAME_LENGTH ? `${name.slice(0, MAX_DISPLAY_NAME_LENGTH)}...` : name;
}

/** Рендер бейджа с порядковым номером участника (для групповой терапии) */
function ParticipantNumberBadge({ number }: { number: number }) {
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black text-sm font-bold text-white"
      aria-hidden
    >
      {number}
    </span>
  );
}

function ParticipantNameBadge({
  clientName,
  remoteFeedObject,
  participantIndex,
  className,
}: {
  clientName?: string;
  remoteFeedObject: RemoteFeedObject;
  participantIndex?: number;
  className?: string;
}) {
  if (!(clientName || remoteFeedObject.display)) return null;

  return (
    <div
      className={classNames(
        'absolute bottom-2 left-2 bg-black/50 rounded-[100px] flex items-center gap-2 px-2 py-1',
        className,
      )}
    >
      {participantIndex != null && <ParticipantNumberBadge number={participantIndex} />}
      <span className="text-xs text-white truncate" title={clientName || remoteFeedObject.display || undefined}>
        {truncateDisplayName(clientName || remoteFeedObject.display || '')}
      </span>
      <MicroIcon className="w-4 h-4 shrink-0" checked={remoteFeedObject.isAudioMuted ?? false} needHover={false} />
    </div>
  );
}

export function RemoteVideo({
  remoteFeedObject,
  displayMode,
  stayOpenInCompactModeSwitch = true,
  clientName,
  hideFitToggle = false,
  participantIndex,
  isHandRaised = false,
}: {
  remoteFeedObject: RemoteFeedObject;
  displayMode: 'compact' | 'equal' | 'default' | 'side-by-side' | 'right-column';
  stayOpenInCompactModeSwitch?: boolean;
  clientName?: string;
  /** Скрыть кнопку «Без увеличения» / «Во весь экран» (в групповых терапиях) */
  hideFitToggle?: boolean;
  /** Порядковый номер участника в групповой терапии (1, 2, 3…); слева от имени показывается цифра */
  participantIndex?: number;
  isHandRaised?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prevPositionRef = useRef({ x: 50, y: 50 });
  const isModelsLoadedRef = useRef<boolean>(false);
  const isCompact = displayMode === 'compact';
  const isEqual = displayMode === 'equal';
  const isScreenShare = remoteFeedObject.isScreenShare;
  const [fitMode, setFitMode] = useState(() => localStorage.getItem('fitMode') || 'cover');
  const [objectPosition, setObjectPosition] = useState('50% 50%');
  const [isOpenVideo, setIsOpenVideo] = useState(true);
  const [isVideoOff, setIsVideoOff] = useState(Boolean(remoteFeedObject.isVideoMuted));
  const [isActiveSpeaker, setIsActiveSpeaker] = useState<boolean>(Boolean(remoteFeedObject.isActiveSpeaker));
  const showActiveSpeakerFrame = isActiveSpeaker && (isOpenVideo || !isCompact);
  const activeSpeakerBorderRadiusClass = (() => {
    if (displayMode === 'right-column' || displayMode === 'side-by-side') {
      return 'rounded-[10px]';
    }

    return 'rounded-[15px]';
  })();
  const activeSpeakerFrameClass = classNames(
    'pointer-events-none absolute inset-0 border-[3px] border-[rgba(255,215,0,0.3)] shadow-[0_0_20px_rgba(255,215,0,0.9)] transition-opacity duration-800 z-[5]',
    activeSpeakerBorderRadiusClass,
    {
      'opacity-100': showActiveSpeakerFrame,
      'opacity-0': !showActiveSpeakerFrame,
    },
  );

  useEffect(() => {
    setIsVideoOff(Boolean(remoteFeedObject.isVideoMuted));
  }, [remoteFeedObject.isVideoMuted]);

  useEffect(() => {
    if (!isCompact) {
      setIsOpenVideo(true);
    }

    if (!stayOpenInCompactModeSwitch && isCompact) {
      setIsOpenVideo(false);
    }
  }, [isCompact, stayOpenInCompactModeSwitch]);

  useEffect(() => {
    const canvas = document.getElementById('canvas-blur');
    const label = document.getElementById('label-blur');
    if (canvas) {
      canvas.style.visibility = isOpenVideo ? 'visible' : 'hidden';
    }
    if (label) {
      label.style.visibility = isOpenVideo ? 'visible' : 'hidden';
    }
  }, [isOpenVideo]);

  const isFaceTrackingEnabled = import.meta.env.VITE_REACT_APP_FACE_TRACKING === 'true';

  useEffect(() => {
    setIsActiveSpeaker(Boolean(remoteFeedObject.isActiveSpeaker));

    const handleActiveSpeakerChange = (isActive: boolean) => {
      setIsActiveSpeaker(isActive);
    };

    remoteFeedObject.onActiveSpeakerChange = handleActiveSpeakerChange;

    return () => {
      if (remoteFeedObject.onActiveSpeakerChange === handleActiveSpeakerChange) {
        remoteFeedObject.onActiveSpeakerChange = undefined;
      }
    };
  }, [remoteFeedObject]);

  useEffect(() => {
    localStorage.setItem('fitMode', fitMode);

    if (fitMode !== 'cover' || !isFaceTrackingEnabled || remoteFeedObject.isScreenShare) {
      setObjectPosition('50% 50%');
      prevPositionRef.current = { x: 50, y: 50 };

      return;
    }

    const loadAndTrack = async () => {
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');

      isModelsLoadedRef.current = true;
    };

    const updateFacePosition = async () => {
      const video = videoRef.current;

      if (
        !isModelsLoadedRef.current ||
        !video ||
        video.readyState < 2 ||
        video.videoWidth === 0 ||
        video.videoHeight === 0
      ) {
        return;
      }

      const result = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions());

      if (result && fitMode === 'cover') {
        const { x, y, width, height } = result.box;
        const xCenter = x + width / 2;
        const yCenter = y + height / 2;
        const xPercent = (xCenter / video.videoWidth) * 100;
        const yPercent = (yCenter / video.videoHeight) * 100;
        const dx = Math.abs(xPercent - prevPositionRef.current.x);
        const dy = Math.abs(yPercent - prevPositionRef.current.y);
        if (dx > 10 || dy > 5) {
          setObjectPosition(`${xPercent}% ${yPercent}%`);
          prevPositionRef.current = { x: xPercent, y: yPercent };
        }
      }
    };

    const intervalId = setInterval(updateFacePosition, 1500);

    loadAndTrack();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fitMode, isFaceTrackingEnabled, remoteFeedObject.isScreenShare]);

  const videoToggleHandler = useCallback((isMuted: 'off' | 'mute' | 'on' | 'unmute') => {
    if (isMuted === 'off' || isMuted === 'mute') {
      console.debug('Handling remote video muted: mode:', isMuted);
      setIsVideoOff(true);
    } else if (isMuted === 'on' || isMuted === 'unmute') {
      console.debug('Handling remote video unmuted');
      setIsVideoOff(false);
    }
  }, []);

  const audioOutputChangeHandler = useCallback((deviceId: string) => {
    if (videoRef.current?.setSinkId) {
      videoRef.current?.setSinkId(deviceId);
    }
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoRef?.current) {
      remoteFeedObject?.attachVideo(videoRef?.current);
      remoteFeedObject.onVideoToggle = videoToggleHandler;
      remoteFeedObject.onAudioOutputChange = audioOutputChangeHandler;
      videoRef?.current?.setAttribute('playsinline', '');

      // Камера могла быть выключена до подписки на onVideoToggle — показываем off-UI сразу
      if (remoteFeedObject.isVideoMuted) {
        videoToggleHandler('off');
      }
    }

    return () => {
      if (remoteFeedObject.onVideoToggle === videoToggleHandler) {
        remoteFeedObject.onVideoToggle = undefined;
      }
      if (remoteFeedObject.onAudioOutputChange === audioOutputChangeHandler) {
        remoteFeedObject.onAudioOutputChange = undefined;
      }
      remoteFeedObject?.remove?.(videoElement ?? undefined);
      if (videoElement) {
        videoElement.srcObject = null;
      }
    };
  }, [audioOutputChangeHandler, remoteFeedObject, videoToggleHandler]);

  const videoObjectFit = isScreenShare ? 'contain' : fitMode === 'cover' ? 'cover' : 'contain';

  const cameraOffLayer = (
    <>
      <CameraOffBackdrop
        visible={isVideoOff}
        hasCapturedFrame={false}
        canvasRef={canvasRef}
        className={activeSpeakerBorderRadiusClass}
        objectFit="cover"
      />
      {isOpenVideo && <CameraOffLabel visible={isVideoOff}>Пользователь отключил видео</CameraOffLabel>}
    </>
  );

  if (isEqual && !isCompact) {
    return (
      <div className={classNames('relative w-full h-[50%] z-[21]')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="videoElement"
          className={classNames(
            'w-full h-full hide-video-controls transition-all duration-500 ease-in-out will-change-[object-fit,object-position] rounded-[15px]',
            isScreenShare ? 'object-contain' : fitMode === 'cover' ? 'object-cover' : 'object-contain',
            { 'rounded-[15px]': isCompact, '!opacity-0 !h-0': !isOpenVideo && isCompact },
          )}
          ref={videoRef}
          controls={false}
          autoPlay
          playsInline
          style={{
            objectPosition: isScreenShare ? 'center' : objectPosition,
          }}
        />
        {cameraOffLayer}
        <div className={activeSpeakerFrameClass}></div>
        <ParticipantNameBadge
          clientName={clientName}
          remoteFeedObject={remoteFeedObject}
          participantIndex={participantIndex}
        />
      </div>
    );
  }

  if (displayMode === 'right-column') {
    return (
      <div className={classNames('relative w-full h-full')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="videoElement"
          className={classNames(
            'w-full h-full hide-video-controls transition-all duration-500 ease-in-out will-change-[object-fit,object-position] rounded-[10px]',
            isScreenShare ? 'object-contain' : 'object-cover',
          )}
          ref={videoRef}
          controls={false}
          autoPlay
          playsInline
          style={{ objectPosition: 'center' }}
        />
        {cameraOffLayer}
        <div className={activeSpeakerFrameClass}></div>
        <ParticipantNameBadge
          clientName={clientName}
          remoteFeedObject={remoteFeedObject}
          participantIndex={participantIndex}
        />
      </div>
    );
  }

  if (displayMode === 'side-by-side') {
    return (
      <div className={classNames('relative w-full h-[120px] pt-[5px]')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="videoElement"
          className={classNames(
            'w-full h-full hide-video-controls transition-all duration-500 ease-in-out will-change-[object-fit,object-position] rounded-[10px]',
            isScreenShare ? 'object-contain' : fitMode === 'cover' ? 'object-cover' : 'object-contain',
          )}
          ref={videoRef}
          controls={false}
          autoPlay
          playsInline
          style={{
            objectPosition: isScreenShare ? 'center' : objectPosition,
          }}
        />
        {cameraOffLayer}
        <div className={activeSpeakerFrameClass}></div>
        <ParticipantNameBadge
          clientName={clientName}
          remoteFeedObject={remoteFeedObject}
          participantIndex={participantIndex}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames('w-full relative overflow-hidden', activeSpeakerBorderRadiusClass, {
        'h-full ': isOpenVideo && !isCompact,
        'h-[100px] lg:h-[180px] max-h-[120px] lg:max-h-[160px] !w-[160px] lg:!w-[280px]': isOpenVideo && isCompact,
        'h-[52px] bg-[#3B3A3F] rounded-[15px] max-h-[42px] !w-[100px]': !isOpenVideo && isCompact,
        'w-[160px] lg:w-[280px] max-w-[280px] z-[21]': isCompact,
      })}
    >
      {isHandRaised && <RaisedHandCornerIndicator />}
      <video
        id="videoElement"
        className={classNames(
          'w-full h-full hide-video-controls transition-all duration-500 ease-in-out will-change-[object-fit,object-position]',
          activeSpeakerBorderRadiusClass,
          { '!opacity-0 !h-0': !isOpenVideo && isCompact },
        )}
        ref={videoRef}
        controls={false}
        autoPlay
        playsInline
        style={{
          objectPosition: isScreenShare ? 'center' : objectPosition,
          objectFit: videoObjectFit,
        }}
      />
      {cameraOffLayer}
      <div className={activeSpeakerFrameClass}></div>
      {isOpenVideo && (
        <ParticipantNameBadge
          clientName={clientName}
          remoteFeedObject={remoteFeedObject}
          participantIndex={participantIndex}
          className="bottom-[10px] left-[15px] bg-greyWithOpacity1_50 px-[10px] py-[5px]"
        />
      )}
      {!isCompact && !hideFitToggle && (
        <button
          onClick={() => setFitMode(fitMode === 'cover' ? 'contain' : 'cover')}
          className="absolute top-2 left-2 z-20 bg-black/60 text-white text-xs px-3 py-1 rounded"
        >
          <span className="flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              {fitMode === 'cover' ? (
                <path d="M6 16h2v2h2v2H6v-4zm8 0h4v4h-4v-2h2v-2zm2-8h-2V6h-2V4h4v4zM6 4h4v2H8v2H6V4z" />
              ) : (
                <path d="M7 14h2v2h2v2H7v-4zm8 0h2v4h-4v-2h2v-2zM7 6h4v2H9v2H7V6zm10 0v4h-2V8h-2V6h4z" />
              )}
            </svg>
            <span>{fitMode === 'cover' ? 'Без увеличения' : 'Во весь экран'}</span>
          </span>
        </button>
      )}
      {isCompact && (
        <div
          className={classNames('absolute top-[9px] right-[15px] cursor-pointer duration-300 ', {
            'rotate-[180deg]': !isOpenVideo,
          })}
          onClick={() => setIsOpenVideo(!isOpenVideo)}
        >
          <ArrowGreaterThan />
        </div>
      )}
    </div>
  );
}
